/**
 * Server state and URL state for the doctor home (docs/ux/dr.md): the
 * doctor's queue, the doctor's upcoming bookings (the clinic window narrowed
 * to one doctor), the admin's doctors list, and one owner of `?doctor`,
 * `?tab`, `?show`, `?from`, `?sort` and `?dir`.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { ApiError, api } from '@/app/api';
import type { ClinicWindow, DoctorCounts, DoctorQueue, DoctorRow } from '@/app/types';
import { addDays, weekKey } from '@/features/clinic/describe';
import { dueWithin48h } from '@/features/qa/useQaQueues';
import { localDateKey } from '@/lib/format';

export const doctorQueueKey = (slug: string) => ['dr', 'queue', slug] as const;
export const doctorCountsKey = ['dr', 'doctors'] as const;

/** null means the slug is unknown to the registry and has no matters (the route's 404). */
export function useDoctorQueue(slug: string | null) {
  return useQuery({
    queryKey: doctorQueueKey(slug ?? ''),
    enabled: slug !== null,
    queryFn: async (): Promise<DoctorQueue | null> => {
      try {
        return await api<DoctorQueue>(`/api/dr/queue?doctor=${encodeURIComponent(slug!)}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    staleTime: 10_000,
    retry: (count) => count < 1,
  });
}

export function useDoctorCounts(enabled: boolean) {
  return useQuery({ queryKey: doctorCountsKey, enabled, queryFn: () => api<DoctorCounts[]>('/api/dr/doctors'), staleTime: 10_000, retry: (count) => count < 1 });
}

export type DrTab = 'reports' | 'bookings' | 'profile';
export type DrShow = 'all' | 'today';
/** `from` is the first day of the bookings window (YYYY-MM-DD, local calendar); null means today. */
export type DrFilters = { doctor: string | null; tab: DrTab; show: DrShow; from: string | null; sort: string | null; dir: 'asc' | 'desc' };

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

export function parseDrFilters(params: URLSearchParams): DrFilters {
  const tab = params.get('tab');
  const from = params.get('from');
  return {
    doctor: params.get('doctor') || null,
    tab: tab === 'profile' || tab === 'bookings' ? tab : 'reports',
    show: params.get('show') === 'today' ? 'today' : 'all',
    from: from && DAY_KEY.test(from) ? from : null,
    sort: params.get('sort') || null,
    dir: params.get('dir') === 'desc' ? 'desc' : 'asc',
  };
}

export function serialiseDrFilters(f: DrFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.doctor) p.set('doctor', f.doctor);
  if (f.tab !== 'reports') p.set('tab', f.tab);
  if (f.show === 'today') p.set('show', 'today');
  if (f.from) p.set('from', f.from);
  if (f.sort) { p.set('sort', f.sort); if (f.dir === 'desc') p.set('dir', 'desc'); }
  return p;
}

export function useDrFilters() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseDrFilters(params), [params]);
  const update = useCallback((patch: Partial<DrFilters>) => {
    setParams((prev) => serialiseDrFilters({ ...parseDrFilters(prev), ...patch }), { replace: true });
  }, [setParams]);
  return { filters, update };
}

/** The bookings window: seven days from `from` (today when null), as the clinic reads a week. */
export function bookingsWindow(from: string | null, now: Date = new Date()): { from: string; to: string; start: Date } {
  const start = from ? new Date(`${from}T00:00:00`) : new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return { from: weekKey(start), to: weekKey(addDays(start, 7)), start };
}

export const isToday = (key: string, now: Date = new Date()): boolean => key === localDateKey(now);

export const bookingsKey = (slug: string, from: string, to: string) => ['clinic', 'doctor', slug, from, to] as const;

/** One doctor's appointments in the window: the clinic's window narrowed by `doctor` (docs/ux/dr.md, the Upcoming bookings tab). */
export function useDoctorBookings(slug: string, from: string | null) {
  const w = bookingsWindow(from);
  return useQuery({
    queryKey: bookingsKey(slug, w.from, w.to),
    queryFn: () => api<ClinicWindow>(`/api/clinic/appointments?from=${w.from}&to=${w.to}&doctor=${encodeURIComponent(slug)}`),
    staleTime: 10_000,
    retry: (count) => count < 1,
  });
}

/** Today: waiting on the doctor and overdue, due within 48 hours or flagged Urgent (docs/ux/dr.md section 2). */
export function isDueToday(row: DoctorRow, now: Date = new Date()): boolean {
  return row.waitingOn === 'doctor' && (row.slaState === 'overdue' || Boolean(row.flags.urgent) || dueWithin48h(row.dueDate, now));
}

export function todayRows(rows: DoctorRow[], now: Date = new Date()): DoctorRow[] {
  return rows.filter((r) => isDueToday(r, now));
}

export const workspaceHref = (ref: string): string => `/matters/${encodeURIComponent(ref)}/workspace`;

/** A bridge doctor's matter at 6 opens the Bridge tab (record, or see where the scribe is); everything else opens the workspace. */
export const onBridgeRow = (row: DoctorRow, doctorOnBridge: boolean): boolean => row.status === 6 && (doctorOnBridge || row.bridge !== 'none');
export const doctorRowHref = (row: DoctorRow, doctorOnBridge: boolean): string => (onBridgeRow(row, doctorOnBridge) ? `/matters/${encodeURIComponent(row.ref)}?tab=bridge` : workspaceHref(row.ref));
